export type UserRole = 'founder' | 'mentor' | 'admin' | 'investor' | 'partner' | 'associate';

export interface NotificationPrefs {
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyDigest: boolean;
  mentorUpdates: boolean;
  courseUpdates: boolean;
}

export interface PrivacyPrefs {
  profileVisibility: boolean;
  activityStatus: boolean;
  dataAnalytics: boolean;
}

export interface CurrentUser {
  id: string;
  name: string;
  role: UserRole;
  avatar: string;
  startupId?: string;
  isFocalTrainee?: boolean;
  inviteToken?: string;
  startups?: string[];
  permissions?: string[];
  bio?: string;
  company?: string;
  title?: string;
  expertise?: string[];
  experience?: string;
  specialties?: string[];
  region?: string;
  industry?: string[];
  // Founder-specific convenience: which plan they picked.
  plan?: 'solo' | 'team';
  // Account lifecycle status mirrored from backend; used by route guards.
  status?: 'active' | 'pending' | 'approved' | 'rejected' | 'suspended';
  // Role subdocs mirrored from backend so route guards can detect incomplete
  // profiles without re-fetching /me on every app entry.
  founder?:   { plan?: 'solo' | 'team'; stage?: string; startupName?: string };
  mentor?:    { availabilitySet?: boolean };
  partner?:   { institution?: string; titleRole?: string; studentCount?: string };
  associate?: { organization?: string; titleRole?: string; purpose?: string };
  investor?:  { fundName?: string; titleRole?: string; checkSize?: string };
  preferences?: {
    notifications: NotificationPrefs;
    privacy: PrivacyPrefs;
  };
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const loadFromStorage = (): CurrentUser | null => {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CurrentUser & { _loginAt?: number };
    if (parsed._loginAt && Date.now() - parsed._loginAt > THIRTY_DAYS_MS) {
      localStorage.removeItem('currentUser');
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

let _currentUser: CurrentUser | null = loadFromStorage();

const defaultUser: CurrentUser = {
  id: '',
  name: '',
  role: 'founder',
  avatar: '',
};

export const getCurrentUser = (): CurrentUser => _currentUser || defaultUser;

export const getCurrentUserRole = (): UserRole => getCurrentUser().role;

export const setCurrentUser = (profile: {
  _id: string;
  name: string;
  role: UserRole;
  avatar?: string;
  startupId?: string;
  isFocalTrainee?: boolean;
  inviteToken?: string;
  startups?: string[];
  permissions?: string[];
  bio?: string;
  company?: string;
  title?: string;
  expertise?: string[];
  experience?: string;
  specialties?: string[];
  region?: string;
  industry?: string[];
  plan?: 'solo' | 'team' | null | undefined;
  status?: 'active' | 'pending' | 'approved' | 'rejected' | 'suspended';
  founder?:   { plan?: 'solo' | 'team'; stage?: string; startupName?: string };
  mentor?:    { availabilitySet?: boolean };
  partner?:   { institution?: string; titleRole?: string; studentCount?: string };
  associate?: { organization?: string; titleRole?: string; purpose?: string };
  investor?:  { fundName?: string; titleRole?: string; checkSize?: string };
  preferences?: {
    notifications: NotificationPrefs;
    privacy: PrivacyPrefs;
  };
}) => {
  _currentUser = {
    id: profile._id,
    name: profile.name,
    role: profile.role,
    avatar: profile.avatar || '',
    startupId: profile.startupId,
    isFocalTrainee: profile.isFocalTrainee,
    inviteToken: profile.inviteToken,
    startups: profile.startups,
    permissions: profile.permissions,
    bio: profile.bio,
    company: profile.company,
    title: profile.title,
    expertise: profile.expertise,
    experience: profile.experience,
    specialties: profile.specialties,
    region: profile.region,
    industry: profile.industry,
    plan: profile.plan || undefined,
    status: profile.status,
    founder:   profile.founder,
    mentor:    profile.mentor,
    partner:   profile.partner,
    associate: profile.associate,
    investor:  profile.investor,
    preferences: profile.preferences,
  };
  try {
    localStorage.setItem('currentUser', JSON.stringify({ ..._currentUser, _loginAt: Date.now() }));
  } catch {}
};

export const clearCurrentUser = () => {
  _currentUser = null;
  try {
    localStorage.removeItem('currentUser');
  } catch {}
};
